import React, { useRef, useEffect } from 'react';

export interface ChartNoteProps {
  isLiveTest?: boolean;
  demoValues?: {
    note: string;
    medications: string;
    homeMeds: string;
    allergies: string;
    labs: string;
    focusedField: string | null;
    mouseClickTarget?: string | null;
  };
  onReset?: () => void;
}

const CRNNote: React.FC<ChartNoteProps> = ({ isLiveTest, demoValues }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const medicationsRef = useRef<HTMLDivElement>(null);
  const homeMedsRef = useRef<HTMLDivElement>(null);
  const allergiesRef = useRef<HTMLDivElement>(null);
  const labsRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const handleCommand = (command: string, value?: string) => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, value);
  };

  const focusEditor = () => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  useEffect(() => {
    if (editorRef.current) {
      setTimeout(() => {
        editorRef.current?.focus({ preventScroll: true });
        document.execCommand('fontName', false, 'Tahoma');
        if (!isLiveTest) {
          document.execCommand('fontSize', false, '2');
        } else {
          document.execCommand('fontSize', false, '5');
        }
      }, 10);
    }
  }, [isLiveTest]);

  useEffect(() => {
    if (!demoValues) return;
    if (editorRef.current) {
      const html = demoValues.note
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      editorRef.current.innerHTML = html || '';
    }
  }, [demoValues?.note]);

  useEffect(() => {
    if (!demoValues) return;
    if (medicationsRef.current) medicationsRef.current.innerText = demoValues.medications;
  }, [demoValues?.medications]);

  useEffect(() => {
    if (!demoValues) return;
    if (homeMedsRef.current) homeMedsRef.current.innerText = demoValues.homeMeds;
  }, [demoValues?.homeMeds]);

  useEffect(() => {
    if (!demoValues) return;
    if (allergiesRef.current) allergiesRef.current.innerText = demoValues.allergies;
  }, [demoValues?.allergies]);

  useEffect(() => {
    if (!demoValues) return;
    if (labsRef.current) labsRef.current.innerText = demoValues.labs;
  }, [demoValues?.labs]);

  useEffect(() => {
    if (!demoValues?.focusedField) return;
    const refMap: Record<string, React.RefObject<HTMLElement | null>> = {
      note: editorRef,
      medications: medicationsRef,
      homeMeds: homeMedsRef,
      allergies: allergiesRef,
      labs: labsRef,
    };
    const ref = refMap[demoValues.focusedField];
    if (ref?.current) {
      ref.current.focus();
    }
  }, [demoValues?.focusedField]);

  const ff = demoValues?.focusedField;

  const navLinks = [
    { section: 'Documentation', items: [
      { label: 'Progress Notes', icon: 'fa-file-pen', active: true },
      { label: 'Discharge Summary', icon: 'fa-file-medical' },
      { label: 'Consultation Notes', icon: 'fa-stethoscope' },
      { label: 'Orders', icon: 'fa-clipboard-list' },
      { label: 'Results', icon: 'fa-flask' },
    ]},
    { section: 'Clinical Data', items: [
      { label: 'Vital Signs', icon: 'fa-heart-pulse' },
      { label: 'Problem List', icon: 'fa-list' },
      { label: 'Medication List', icon: 'fa-pills' },
      { label: 'Immunizations', icon: 'fa-syringe' },
    ]},
    { section: 'Communication', items: [
      { label: 'Messages', icon: 'fa-envelope' },
      { label: 'Referrals', icon: 'fa-share-from-square' },
    ]},
    { section: 'Recent Activity', items: [
      { label: 'Audit Trail', icon: 'fa-clock' },
      { label: 'Print History', icon: 'fa-print' },
      { label: 'Signatures', icon: 'fa-file-signature' },
    ]},
  ];

  return (
    <div className="h-full bg-[#f0f0f0] flex flex-col font-['Tahoma',sans-serif] overflow-hidden text-[11px] relative">
      <div className="absolute inset-0 z-40 cursor-default" />
      {/* Menu Bar */}
      <div className="bg-[#f0f0f0] border-b border-gray-300 px-2 py-0.5 flex space-x-3 items-center text-gray-800 h-6 shrink-0">
        {['Task', 'Edit', 'View', 'Patient', 'Chart', 'Links', 'Notifications', 'Time Scale', 'Options', 'Help'].map(item => (
          <span key={item} className="hover:bg-blue-100 px-1 cursor-default">{item}</span>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-[#f0f0f0] border-b border-gray-400 px-2 py-1 flex items-center space-x-4 h-8 shrink-0">
        <div className="flex items-center space-x-1 border-r border-gray-400 pr-2">
          <i className="fa-solid fa-house text-blue-700"></i>
          <span>Home</span>
        </div>
        <div className="flex items-center space-x-2 border-r border-gray-400 pr-2">
          <i className="fa-solid fa-users text-gray-600"></i>
          <span>Patient List</span>
        </div>
        <div className="flex items-center space-x-2 border-r border-gray-400 pr-2">
          <i className="fa-solid fa-user-doctor text-gray-600"></i>
          <span>Physician Worklist</span>
        </div>
        <div className="flex items-center space-x-2">
          <i className="fa-solid fa-arrow-up-right-from-square text-green-600"></i>
          <span>Med Lookup</span>
        </div>
        <div className="flex-1"></div>
        <div className="flex items-center space-x-2 text-[10px]">
          <span className="text-yellow-700 font-bold">Criti: 0</span>
          <span className="text-gray-500">eRx R: 0</span>
          <span className="font-bold text-red-700">Error</span>
        </div>
      </div>

      {/* Patient Banner */}
      <div className="bg-[#007cc3] text-white flex flex-col shrink-0 h-[68px]">
        <div className="flex items-center flex-1 px-3">
          <div className="flex items-center space-x-3 min-w-[220px]">
            <div className="w-9 h-9 bg-blue-100 rounded-sm flex items-center justify-center text-blue-800">
              <i className="fa-solid fa-user text-xl"></i>
            </div>
            <div>
              <div className="font-bold text-base leading-none">Sarah Mitchell</div>
              <div className="text-[10px] bg-red-800 px-1 mt-1 inline-block uppercase font-semibold">Allergies: Penicillin, Sulfa</div>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-2 text-[10px] border-l border-white/20 pl-4 h-full py-1">
            <div><div>DOB: 03/14/1978</div><div>Age: 47 years</div></div>
            <div><div>Dose Wt: 62.500 kg (04/06/2026)</div><div>Inpatient FIN: 2600410882</div></div>
            <div><div>Sex: Female</div><div>MRN: 78031452</div></div>
            <div><div>Attending: Andrea Athelas MD</div><div className="font-bold">Loc: 4W Neurolonav: 4412: 0</div></div>
          </div>
        </div>
      </div>

      {/* Allergies Bar */}
      <div className="bg-[#fff9c4] px-4 py-1 border-b border-yellow-400 shrink-0 text-[10px] flex items-center">
        <span className="font-bold text-red-700 mr-1">ALLERGIES:</span>
        <span className="text-red-600 font-semibold">Penicillin: Anaphylaxis; Sulfa: Rash - Generalised</span>
      </div>

      {/* 3-Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Icon Sidebar */}
        <div className="w-12 bg-[#334d66] text-white flex flex-col items-center py-2 space-y-3 shrink-0 border-r border-gray-500">
          <div className="flex flex-col items-center cursor-pointer hover:bg-white/10 w-full py-1.5">
            <i className="fa-solid fa-file-circle-plus text-[14px] mb-0.5"></i>
            <span className="text-[8px]">New</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer bg-white/20 w-full py-1.5 border-l-2 border-white">
            <i className="fa-solid fa-file-lines text-[14px] mb-0.5"></i>
            <span className="text-[8px]">Current</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer hover:bg-white/10 w-full py-1.5">
            <i className="fa-solid fa-clipboard-list text-[14px] mb-0.5"></i>
            <span className="text-[8px]">Mini-EPR</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer hover:bg-white/10 w-full py-1.5">
            <i className="fa-solid fa-list-check text-[14px] mb-0.5"></i>
            <span className="text-[8px]">To-Do</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer hover:bg-white/10 w-full py-1.5">
            <i className="fa-solid fa-clock-rotate-left text-[14px] mb-0.5"></i>
            <span className="text-[8px]">History</span>
          </div>
        </div>

        {/* Middle Navigation Panel */}
        <div className="w-[180px] bg-white border-r border-gray-300 flex flex-col shrink-0 overflow-hidden">
          <div className="bg-gray-100 px-3 py-1.5 font-bold border-b border-gray-300 flex justify-between items-center">
            Navigation Panel
            <i className="fa-solid fa-chevron-right text-[8px] text-gray-400"></i>
          </div>
          <div className="p-2 border-b border-gray-200">
            <div className="flex items-center border border-gray-300 rounded-sm px-2 py-0.5 bg-white">
              <input type="text" placeholder="Quick Search" className="flex-1 outline-none text-[10px] bg-transparent" />
              <i className="fa-solid fa-magnifying-glass text-[9px] text-gray-400"></i>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto legacy-scrollbar">
            {navLinks.map((group) => (
              <React.Fragment key={group.section}>
                <div className="bg-gray-50 px-2 py-1.5 border-b border-gray-200 flex items-center cursor-pointer font-semibold">
                  <i className="fa-solid fa-chevron-down text-[8px] mr-1.5"></i> {group.section}
                </div>
                {group.items.map((item) => (
                  <div
                    key={item.label}
                    className={`px-5 py-1.5 border-b border-gray-100 cursor-pointer text-[10px] ${
                      item.active ? 'bg-[#fff9c4] text-blue-700 font-semibold' : 'text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`fa-solid ${item.icon} text-[9px] mr-1.5 ${item.active ? '' : 'text-gray-400'}`}></i>
                    {item.label}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 flex flex-col bg-white overflow-hidden">
          {/* Breadcrumb */}
          <div className="bg-[#003d5c] text-white flex items-center h-7 px-2 shrink-0 space-x-2">
            <i className="fa-solid fa-bars text-[10px]"></i>
            <i className="fa-solid fa-house text-[10px]"></i>
            <i className="fa-solid fa-caret-right text-[10px]"></i>
            <span className="font-bold">Documentation</span>
            <i className="fa-solid fa-caret-right text-[10px]"></i>
            <span>Progress Notes</span>
            <div className="flex-1"></div>
            <span className="text-[10px] cursor-pointer"><i className="fa-solid fa-maximize mr-1"></i>Full screen</span>
            <span className="text-[10px] cursor-pointer"><i className="fa-solid fa-print mr-1"></i>Print</span>
            <span className="text-[10px] text-gray-300">1 minutes ago</span>
          </div>

          {/* Add Toolbar */}
          <div className="flex items-center space-x-1 border-b border-gray-300 px-2 py-0.5 shrink-0 bg-[#f0f0f0]">
            <button className="flex items-center px-1.5 py-0.5 hover:bg-gray-200 border border-transparent hover:border-gray-400">
              <i className="fa-solid fa-plus text-blue-600 mr-1"></i> Add
            </button>
            <button onClick={() => window.location.reload()} className="hover:bg-gray-200 p-1 rounded-sm">
              <i className="fa-solid fa-rotate-right text-gray-600 text-[10px]"></i>
            </button>
            <button className="hover:bg-gray-200 p-1 rounded-sm">
              <i className="fa-solid fa-floppy-disk text-gray-600 text-[10px]"></i>
            </button>
            <div className="flex-1"></div>
            <span className="text-[10px] text-gray-500">Author: Andrea Athelas MD | Status: In Progress</span>
          </div>

          {/* Note Tabs */}
          <div className="flex items-end h-6 border-b border-gray-300 shrink-0 bg-[#f0f0f0] px-1">
            <div className="bg-white border-t border-x border-gray-300 px-3 py-0.5 font-bold text-gray-800 -mb-px flex items-center">
              Progress Note <span className="ml-2 font-normal text-[10px]">&#215;</span>
            </div>
            <div className="bg-gray-100 border-t border-x border-gray-300 px-3 py-0.5 text-gray-600 cursor-pointer">List</div>
          </div>

          {/* Rich Text Toolbar */}
          <div className="bg-[#f0f0f0] border-b border-gray-300 p-1 flex items-center space-x-1 shrink-0 min-h-[28px]">
            <select onChange={(e) => handleCommand('fontName', e.target.value)} className="bg-white border border-gray-400 px-1 h-[20px] min-w-[80px] text-[10px] focus:outline-none" defaultValue="Tahoma">
              <option value="Tahoma">Tahoma</option>
              <option value="Arial">Arial</option>
              <option value="Segoe UI">Segoe UI</option>
              <option value="Courier New">Courier New</option>
              <option value="Times New Roman">Times New Roman</option>
            </select>
            <select onChange={(e) => handleCommand('fontSize', e.target.value)} className="bg-white border border-gray-400 px-1 h-[20px] min-w-[45px] text-[10px] focus:outline-none" defaultValue="2">
              <option value="1">Small</option>
              <option value="2">Normal</option>
              <option value="3">Large</option>
              <option value="4">Extra Large</option>
              <option value="5">Heading 2</option>
              <option value="6">Heading 1</option>
            </select>
            <div className="h-4 border-r border-gray-400 mx-0.5"></div>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('bold'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center font-bold text-[11px] border border-transparent hover:border-gray-400">B</button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('italic'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center italic text-[11px] border border-transparent hover:border-gray-400">I</button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('underline'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center underline underline-offset-2 text-[11px] border border-transparent hover:border-gray-400">U</button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('strikethrough'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center line-through text-[9px] border border-transparent hover:border-gray-400">abc</button>
            <div className="relative">
              <button onMouseDown={(e) => { e.preventDefault(); colorInputRef.current?.click(); }} className="w-6 h-5 hover:bg-gray-300 flex items-center justify-center text-orange-600 font-bold border border-transparent hover:border-gray-400 text-[10px]">
                A<i className="fa-solid fa-caret-down text-[5px] ml-0.5"></i>
              </button>
              <input type="color" ref={colorInputRef} className="absolute opacity-0 pointer-events-none w-0 h-0" onChange={(e) => handleCommand('foreColor', e.target.value)} />
            </div>
            <div className="h-4 border-r border-gray-400 mx-0.5"></div>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('justifyLeft'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center border border-transparent hover:border-gray-400"><i className="fa-solid fa-align-left text-[9px]"></i></button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('justifyCenter'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center border border-transparent hover:border-gray-400"><i className="fa-solid fa-align-center text-[9px]"></i></button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('justifyRight'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center border border-transparent hover:border-gray-400"><i className="fa-solid fa-align-right text-[9px]"></i></button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('justifyFull'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center border border-transparent hover:border-gray-400"><i className="fa-solid fa-align-justify text-[9px]"></i></button>
            <div className="h-4 border-r border-gray-400 mx-0.5"></div>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('insertUnorderedList'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center border border-transparent hover:border-gray-400"><i className="fa-solid fa-list-ul text-[9px]"></i></button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('insertOrderedList'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center border border-transparent hover:border-gray-400"><i className="fa-solid fa-list-ol text-[9px]"></i></button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('outdent'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center border border-transparent hover:border-gray-400"><i className="fa-solid fa-outdent text-[9px]"></i></button>
            <button onMouseDown={(e) => { e.preventDefault(); handleCommand('indent'); }} className="w-5 h-5 hover:bg-gray-300 flex items-center justify-center border border-transparent hover:border-gray-400"><i className="fa-solid fa-indent text-[9px]"></i></button>
          </div>

          {/* Editor + Sidebar */}
          <div className="flex-1 flex overflow-hidden">
            {/* Main Note Editor */}
            <div className="flex-1 overflow-y-auto p-3 border-r border-gray-300 bg-white legacy-scrollbar cursor-text" onClick={focusEditor}>
              <div
                id="chart-note-editor"
                ref={editorRef}
                className={`w-full outline-none font-['Tahoma',sans-serif] leading-normal text-gray-900 caret-transparent ${isLiveTest ? 'text-2xl' : 'text-[12px]'}`}
                style={{ minHeight: '600px' }}
                contentEditable
                spellCheck="false"
                data-placeholder=""
                onKeyDown={(e) => {
                  if (e.currentTarget.innerHTML === '' || e.currentTarget.innerHTML === '<br>') {
                    document.execCommand('fontName', false, 'Tahoma');
                    if (!isLiveTest) {
                      document.execCommand('fontSize', false, '2');
                    } else {
                      document.execCommand('fontSize', false, '5');
                    }
                  }
                }}
              ></div>
            </div>

            {/* Right Sidebar */}
            <div className="w-[220px] bg-gray-50 overflow-y-auto p-2 text-[10px] legacy-scrollbar shrink-0 flex flex-col space-y-2">
              {/* Medications (Inpatient) */}
              <div className="border border-gray-300 rounded-sm bg-white overflow-hidden">
                <div className="bg-gray-100 px-2 py-0.5 font-bold border-b border-gray-300 flex justify-between items-center">
                  <span><i className="fa-solid fa-pills text-[9px] mr-1 text-blue-600"></i>Medications (Inpatient)</span>
                  <div className="flex space-x-1 opacity-50"><i className="fa-solid fa-rotate-right text-[8px]"></i><i className="fa-solid fa-chevron-down text-[8px]"></i></div>
                </div>
                <div ref={medicationsRef} contentEditable spellCheck="false" className={`p-1.5 h-[64px] overflow-hidden outline-none whitespace-pre-wrap caret-transparent ${ff === 'medications' ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-white'}`}></div>
              </div>

              {/* Home Meds */}
              <div className="border border-gray-300 rounded-sm bg-white overflow-hidden">
                <div className="bg-gray-100 px-2 py-0.5 font-bold border-b border-gray-300 flex justify-between items-center">
                  <span><i className="fa-solid fa-house-medical text-[9px] mr-1 text-green-600"></i>Home Meds</span>
                  <div className="flex space-x-1 opacity-50"><i className="fa-solid fa-rotate-right text-[8px]"></i><i className="fa-solid fa-chevron-down text-[8px]"></i></div>
                </div>
                <div ref={homeMedsRef} contentEditable spellCheck="false" className={`p-1.5 h-[64px] overflow-hidden outline-none whitespace-pre-wrap caret-transparent ${ff === 'homeMeds' ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-white'}`}></div>
              </div>

              {/* Allergies */}
              <div className="border border-gray-300 rounded-sm bg-white overflow-hidden">
                <div className="bg-gray-100 px-2 py-0.5 font-bold border-b border-gray-300 flex justify-between items-center">
                  <span><i className="fa-solid fa-triangle-exclamation text-[9px] mr-1 text-red-500"></i>Allergies</span>
                  <div className="flex space-x-1 opacity-50"><i className="fa-solid fa-rotate-right text-[8px]"></i><i className="fa-solid fa-chevron-down text-[8px]"></i></div>
                </div>
                <div ref={allergiesRef} contentEditable spellCheck="false" className={`p-1.5 h-[64px] overflow-hidden outline-none whitespace-pre-wrap caret-transparent ${ff === 'allergies' ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-white'}`}></div>
              </div>

              {/* Lab Results */}
              <div className="border border-gray-300 rounded-sm bg-white overflow-hidden">
                <div className="bg-gray-100 px-2 py-0.5 font-bold border-b border-gray-300 flex justify-between items-center">
                  <span><i className="fa-solid fa-flask-vial text-[9px] mr-1 text-purple-600"></i>Lab Results (Last 24 Hours)</span>
                  <div className="flex space-x-1 opacity-50"><i className="fa-solid fa-rotate-right text-[8px]"></i><i className="fa-solid fa-chevron-down text-[8px]"></i></div>
                </div>
                <div ref={labsRef} contentEditable spellCheck="false" className={`p-1.5 h-[64px] overflow-hidden outline-none whitespace-pre-wrap caret-transparent ${ff === 'labs' ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-white'}`}></div>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="h-8 bg-[#f0f0f0] border-t border-gray-400 p-1 flex items-center justify-end shrink-0">
            <div className="flex space-x-1">
              <button className="bg-[#e1e1e1] border-t border-l border-white border-b border-r border-gray-600 px-3 py-0.5 text-[10px] font-sans active:border-t-gray-600 active:border-l-gray-600 active:shadow-inner">Sign/Submit</button>
              <button className="relative bg-[#e1e1e1] border-t border-l border-white border-b border-r border-gray-600 px-3 py-0.5 text-[10px] font-sans active:border-t-gray-600 active:border-l-gray-600 active:shadow-inner">
                {demoValues?.mouseClickTarget === 'save' && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
                    <div className="relative flex items-center justify-center">
                      <span className="absolute w-10 h-10 rounded-full bg-blue-400/40 animate-ping" />
                      <span className="w-5 h-5 rounded-full bg-blue-500/70 flex items-center justify-center">
                        <svg viewBox="0 0 20 20" className="w-3 h-3 fill-white"><path d="M6 2l.67 8.59L9 8.5l1.5 3.5 1-.5-1.5-3.5 2.59-.41L6 2z"/></svg>
                      </span>
                    </div>
                  </div>
                )}
                Save
              </button>
              <button className="bg-[#e1e1e1] border-t border-l border-white border-b border-r border-gray-600 px-3 py-0.5 text-[10px] font-sans active:border-t-gray-600 active:border-l-gray-600 active:shadow-inner">Save &amp; Close</button>
              <button className="bg-[#e1e1e1] border-t border-l border-white border-b border-r border-gray-600 px-3 py-0.5 text-[10px] font-sans active:border-t-gray-600 active:border-l-gray-600 active:shadow-inner">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRNNote;
